import React, { useState, useEffect } from 'react';
 
import { FaChartLine, FaSignOutAlt, FaBars, FaQuestionCircle, FaUsers } from 'react-icons/fa';
 
import Sidebar_circle_logo from '../../images/Sidebar_circle_logo.svg';
 
import { useNavigate } from 'react-router-dom';


const AdminSidebar = ({ isCollapsed, onToggle, activeLink, setActiveLink, onLogout, onNavigate }) => {
  const [animatingLink, setAnimatingLink] = useState(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const navigate = useNavigate();

  // Handle navigation
  useEffect(() => {
    let timer;
    if (animatingLink) {
      timer = setTimeout(() => setAnimatingLink(null), 400);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [animatingLink]);

  const handleNavClick = (e, linkName, route) => {
    e.preventDefault();

    if (isCollapsed) {
      setAnimatingLink(linkName);
    }

    setActiveLink(linkName);

    if (onNavigate) {
      onNavigate(linkName, route);
    } else if (route) {
      navigate(route);
    }
  };
  
  // Dynamic horizontal spacing for links/buttons
  const horizontalPaddingClass = isCollapsed ? 'px-4' : 'px-6';

  const getLabelClass = () =>
    `text-lg font-medium transition-all duration-300 origin-left whitespace-nowrap ${
      isCollapsed
        ? 'opacity-0 w-0 -translate-x-2 scale-95 pointer-events-none'
        : 'opacity-100 w-full translate-x-0 scale-100'
    }`;

  // Helper function for link styling
  const getLinkClass = (name) => {
    const isActive = activeLink === name;
    
    // Based on the provided mockup (image_8f3649.png):
    // Active = Orange background, White text
    // Inactive = Transparent background, Gray text
    // Hover = Dark gray background, Orange text
    
    const baseClass = `flex items-center px-4 py-3 rounded-lg transition-all duration-300`;
    const alignmentClass = isCollapsed ? 'justify-center w-full' : 'gap-3 justify-start';
    
    const animationClass = animatingLink === name && isCollapsed ? 'animate-pulse' : '';

    if (isActive) {
      return `${baseClass} ${alignmentClass} text-[#E68A40] ${animationClass}`;
    }
    
    return `${baseClass} ${alignmentClass} text-gray-400 hover:bg-[#1d1c24] hover:text-[#E68A40] ${animationClass}`;
  };


  return (
    // Ensure the sidebar always spans the full viewport height
    <aside 
        className={`h-screen bg-[#0C0B10] text-white flex flex-col justify-between transition-all duration-300 pb-6 pt-[56px]`} 
        style={{ width: isCollapsed ? '140px' : '360px' }}
    >
      
      {/* Top Section: Logo & Navigation */}
      <div>
        
        {/* --- HEADER --- */}
        
        {/* Expanded state */}
        {!isCollapsed && (
          <div className="flex items-center justify-between mb-10 pl-[30px] pr-[30px]"> 
            
            {/* Logo (Icon and Text) - Left aligned, not centered */}
            <div className="flex items-center justify-start gap-[10px] select-none cursor-default "> { /* previously gap-[34px] when centered */}
              {/* Logo Icon */}
              <img 
                src={Sidebar_circle_logo} 
                alt="Logo" 
                className="w-[85px] h-[85px] flex-shrink-0 select-none cursor-default"
              />
              {/* Logo Text */}
              <span className="text-2xl font-bold select-none cursor-default">
                <span className="text-[#E68A40]">THE</span>
                <span>ADOX</span>
              </span>
            </div>
            
            {/* Hamburger Button: toggles the sidebar */}
            <button 
              onClick={onToggle}
              className="text-2xl text-gray-400 hover:text-white"
              aria-expanded={!isCollapsed}
            >
              <FaBars />
            </button>
          </div>
        )}

        {/* Collapsed state header */}
        {isCollapsed && (
          <div className="flex flex-col items-center justify-center mb-4 select-none cursor-default"> { /* previously mb-10 when centered */}
            {/* Logo Icon */}
            <img 
              src={Sidebar_circle_logo} 
              alt="Logo" 
              className="w-[85px] h-[85px] flex-shrink-0 mb-6 select-none cursor-default"
            />
            {/* Hamburger Button (toggle) */}
             <button 
              onClick={onToggle}
              className="text-2xl text-gray-400 hover:text-white"
              aria-expanded={!isCollapsed}
            >
              <FaBars />
            </button>
          </div>
        )}
        {/* --- END OF HEADER --- */}


        {/* --- NAVIGATION MENU --- */}
        {!isCollapsed && (
          <nav className={`px-10 mt-4`}>
            <ul className="space-y-3">
              
              {/* Dashboard (Active Link Example) */}
              <li>
                <a 
                  href="/admindashboard"
                  onClick={(e) => handleNavClick(e, 'Dashboard', '/admindashboard')}
                  className={`${getLinkClass('Dashboard')} cursor-pointer select-none`}
                >
                  <FaChartLine className="text-xl flex-shrink-0" />
                  <span className={getLabelClass()}>Dashboard</span>
                </a>
              </li>
              
              {/* Button 2 */}
              <li>
                <a 
                  href="/employees"
                  onClick={(e) => handleNavClick(e, 'Employees', '/employees')}
                  className={`${getLinkClass('Employees')} cursor-pointer select-none`}
                >
                  <FaUsers className="text-xl flex-shrink-0" />
                  <span className={getLabelClass()}>Employees</span>
                </a>
              </li>
              
              {/* Button 3 */}
              <li>
                <a 
                  href="/howtouse"
                  onClick={(e) => handleNavClick(e, 'How to Use', '/howtouse')}
                  className={`${getLinkClass('How to Use')} cursor-pointer select-none`}
                >
                  <FaQuestionCircle className="text-xl flex-shrink-0" />
                  <span className={getLabelClass()}>How to Use</span>
                </a>
              </li>
            </ul>
          </nav>
        )}
      </div>

      {/* Bottom Section: Logout */}
      {!isCollapsed && (
        <div className={`mt-auto ${horizontalPaddingClass}`}>
          <button 
            onClick={() => setShowLogoutConfirm(true)}
            className={`flex items-center p-3 rounded-lg w-full text-left text-gray-400 hover:bg-gray-700 hover:text-white transition-all duration-300 cursor-pointer select-none
                        ${isCollapsed ? 'justify-center' : 'gap-4 justify-start'}`}
          >
            <FaSignOutAlt className="text-xl flex-shrink-0" />
            <span className={getLabelClass()}>Logout</span>
          </button>
        </div>
      )}

      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="bg-[#1D1C21] text-white rounded-2xl p-6 w-full max-w-sm shadow-2xl border border-white/10">
            <h3 className="text-xl font-semibold mb-2">Log out?</h3>
            <p className="text-gray-400 text-sm mb-6">
              You’re about to end your admin session. Do you want to continue?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="px-4 py-2 rounded-lg border border-white/20 text-white hover:bg-white/10 transition-colors"
              >
                Stay Logged In
              </button>
              <button
                onClick={() => {
                  setShowLogoutConfirm(false);
                  onLogout();
                }}
                className="px-4 py-2 rounded-lg bg-[#E68A40] hover:bg-[#D67A30] text-white transition-colors"
              >
                Log Out
              </button>
            </div>
          </div>
        </div>
      )}

    </aside>
  );
};

export default AdminSidebar;